// GA4(dataLayer) と PostHog の両方へ送るドメインイベントの集約。
// GA4のみのイベントは ga.ts、両方へ送るイベントはこのファイル、と置き場所で送信先を示す。
// プロパティを持たないクリック（認証3択など）は PostHog Autocapture に任せ、ここには置かない。

import type { ExploreModeType } from '@/constants';
import { trackDualEvent } from '@/utils/trackDualEvent';
import { type CaptureOptions, EVENT_NAMES, type EventProperties } from '@monorepo/common';

// 押下直後にハードナビ（window.location 遷移）する系で渡す。PostHog を確実に送るための beacon 送信。
// ※ GA4(dataLayer) には効かないため、呼び出し側は遷移の前に送信関数を呼ぶこと（GTM タグ発火の猶予確保）。
export const HARD_NAV_CAPTURE: CaptureOptions = { send_instantly: true, transport: 'sendBeacon' };

// 素材を選ぶ - コマクリック。
export const sendMaterialClickEvent = (params: {
  materialId: number;
  materialName: string;
  workId: number;
  workName: string;
  userId: number;
}) => {
  trackDualEvent(EVENT_NAMES.materialClick, {
    materialId: params.materialId,
    materialName: params.materialName,
    workId: params.workId,
    workName: params.workName,
    user_id: params.userId,
  });
};

// カート - 「この作品でさらにグッズをつくる」。どの作品からの買い周りが効いているかを見る
export const sendClickCartGoWorkDetailEvent = (params: { workId: number; workName: string; userId: number }) => {
  trackDualEvent(EVENT_NAMES.cartGoWorkDetail, {
    workId: params.workId,
    workName: params.workName,
    user_id: params.userId,
  });
};

// TOP - ピックアップバナークリック（position は掲載枠の並び順・0始まり）
export const sendPickupBannerClickEvent = (params: { bannerId: number; position: number; userId: number }) => {
  trackDualEvent(EVENT_NAMES.pickupBannerClick, {
    bannerId: params.bannerId,
    position: params.position,
    user_id: params.userId,
  });
};

// TOP - 探索導線（ヒーローCTA）クリック。exploreMode で入口を区別する
export const sendExploreEntryClickEvent = (params: { exploreMode: ExploreModeType; userId: number }) => {
  trackDualEvent(EVENT_NAMES.exploreEntryClick, {
    exploreMode: params.exploreMode,
    user_id: params.userId,
  });
};

// 認証 - 登録開始（方法ボタン押下・OAuthリダイレクト前）
export const sendSignUpStartEvent = (properties: EventProperties['sign_up_start']) =>
  trackDualEvent(EVENT_NAMES.signUpStart, properties);

// 認証 - 登録完了（サーバー確定後の着地時に1回）
export const sendSignUpEvent = (properties: EventProperties['sign_up']) =>
  trackDualEvent(EVENT_NAMES.signUp, properties);

// 認証 - ログイン成功（着地時に1回）
export const sendLoginEvent = (properties: EventProperties['login']) => trackDualEvent(EVENT_NAMES.login, properties);

// 連携 - 追加確定。マイページ/購入完了の連携ルートはハードナビ直前のため HARD_NAV_CAPTURE を渡す
export const sendAccountLinkCompleteEvent = (
  properties: EventProperties['account_link_complete'],
  options?: CaptureOptions,
) => trackDualEvent(EVENT_NAMES.accountLinkComplete, properties, options);

// 連携 - 解除確定（マイページ）
export const sendAccountUnlinkCompleteEvent = (properties: EventProperties['account_unlink_complete']) =>
  trackDualEvent(EVENT_NAMES.accountUnlinkComplete, properties);

// LINE - 連携促進ボタンのクリック（押下→ハードナビ）
export const sendLineLinkClickEvent = (flowOrigin: EventProperties['line_link_click']['flowOrigin']) =>
  trackDualEvent(EVENT_NAMES.lineLinkClick, { flowOrigin }, HARD_NAV_CAPTURE);

// LINE - 友だち追加促進ボタンのクリック（押下→ハードナビ）
export const sendLineFriendClickEvent = (flowOrigin: EventProperties['line_friend_click']['flowOrigin']) =>
  trackDualEvent(EVENT_NAMES.lineFriendClick, { flowOrigin }, HARD_NAV_CAPTURE);
